// @ts-check
// calendars.js — "Calendars" settings section.
//
// Extracted from settings-panel.js (Stage C of the monolith split). Multi-
// provider calendars panel: connected accounts (Google, Microsoft, iCloud,
// CalDAV) as workspace-style rows, per-account expandable rows with per-calendar
// visibility checkboxes + colour pickers, a full-width "＋ Add new calendar" CTA
// that opens the inline add-account flow (CalDAV form / OAuth), and the
// parked-outbox surface.
//
// Fully self-contained — no host wiring touches this section's state, so only
// renderCalendars(ctx) is exported. ctx ({ row, rawRow, openExpansions,
// invalidate, isOpened }) is threaded into the handlers that re-render.
//
// Note: the disconnect-account flow confirms via the in-flow `confirmStatus`
// status-pill prompt (ui/status-pill.ts), matching the other settings sections.

import { CalendarOAuth } from '../../schedule/auth/oauth-web.js';
import { CalendarOutbox } from '../../schedule/outbox.js';
import { ProviderRegistry } from '../../schedule/provider-registry.js';
import { invalidateScheduleCache } from '../../schedule/schedule.js';
import { confirmStatus } from '../../ui/status-pill.js';
import { WorkspaceSessionVault } from '../../workspaces/workspace-session-vault.js';
import type { SettingsSectionCtx } from '../settings-section-api.js';
import { Settings } from '../settings-store.js';
import {
  addCta,
  buildRevealToggle,
  colorPicker,
  errorDiv,
  expandHeadingCta,
  makeP,
  removeButton,
  setErrorDiv,
  syncRevealIcon,
} from './_common.js';

/** @type {any} { kind, step, error, fields } */
let _newAccountState: any = null;
let _accountsBusy = false;

/**
 * @param {any} ctx - the section render context.
 * @returns {any[]} the section's rows.
 */
export function renderCalendars(ctx: SettingsSectionCtx) {
  const rows = [];
  const ID = 'group:calendars';
  const accounts = Settings?.get('calendars.accounts', []) || [];

  rows.push(
    ctx.row({
      id: ID,
      label: 'Calendars',
      // Right-hand action: an "Add calendar" value button (like Encryption's
      // "Pair" / Sync's "Sign in") that expands the section and opens the
      // provider picker — the heading twin of the body's "＋ Add new calendar".
      control: () => buildAddHeadingCta(ctx, ID),
      expandable: true,
      searchExpand: true,
      cls: 'settings-row-group',
      searchText:
        'calendars events colour accounts add caldav icloud google microsoft not connected',
    })
  );
  if (!ctx.openExpansions.has(ID)) return rows;

  // Empty-state pitch when no accounts are connected.
  if (accounts.length === 0) {
    rows.push(
      ctx.rawRow(
        () => {
          const banner = document.createElement('div');
          banner.className = 'settings-banner';
          banner.textContent = 'Connect a calendar to sync events with your daily notes.';
          return banner;
        },
        { key: 'cal-empty', indent: true, searchText: 'connect calendar empty state' }
      )
    );
  }

  // Sync window: how far back each poll fetches/reconciles events. Only
  // meaningful once a calendar is connected, so it rides under the group with
  // the accounts. Bounds the otherwise-unbounded per-poll scan (calendar-sync
  // _syncWindow); see D015.
  if (accounts.length > 0) {
    rows.push(
      ctx.row({
        id: 'calendars:sync-window',
        label: 'Sync window',
        control: () => buildSyncWindowSelect(),
        updateControl: (el: any) => {
          const cur = String(Number(Settings?.get('calendars.syncLookbackDays', 90)) || 0);
          const sel = el?.tagName === 'SELECT' ? el : el?.querySelector?.('select');
          if (sel && sel.value !== cur) sel.value = cur;
        },
        indent: true,
        searchText:
          'sync window lookback days how far back reconcile events calendar performance history',
      })
    );
  }

  for (const acct of accounts) {
    const subId = `cal-account:${acct.accountId}`;
    const sub = ctx.row({
      id: subId,
      label: acct.label || acct.accountId,
      // NOT a fold group — the calendar-list reveal icon is the only disclosure
      // affordance (the workspace members-icon pattern), so the row shows no fold
      // squiggle and its calendar list carries no nested accent line.
      expandable: false,
      indent: true,
      // Same row chrome as the workspace / device list.
      cls: 'settings-row-workspace',
      // Provider label + a calendar-list reveal icon + inline "×" disconnect.
      control: () => buildAccountControl(ctx, acct),
      updateControl: (el: any) => {
        const reveal = el.querySelector('.settings-cal-reveal') as HTMLElement | null;
        if (reveal)
          syncRevealButton(reveal, ctx.openExpansions.has(subId), acct.label || acct.accountId);
      },
      searchText: `account ${acct.label} ${acct.accountId} ${acct.provider} disconnect remove calendars`,
    });
    rows.push(sub);
    if (ctx.openExpansions.has(subId)) {
      // Per-account body: the calendar list (revealed by the icon).
      rows.push(...renderAccountBody(ctx, acct));
    }
  }

  // ＋ Add new calendar — full-width primary CTA, the same styling as the
  // Workspaces "＋ New workspace" / Encryption "＋ Pair new device" buttons.
  // Clicking opens the provider picker below; hidden while that flow is already
  // open so the picker replaces it in place.
  if (!_newAccountState) {
    rows.push(
      ctx.rawRow(
        () =>
          addCta('＋ Add new calendar', () => {
            _newAccountState = { kind: null, step: 'pick', error: '', fields: {} };
            ctx.invalidate();
          }),
        {
          key: 'cal:add',
          indent: true,
          searchText: 'add new calendar account connect caldav icloud google microsoft',
        }
      )
    );
  } else {
    // The add-account flow (provider picker → CalDAV form / OAuth).
    rows.push(...renderAddAccountForm(ctx));
  }

  // ── Parked outbox entries ──
  // After 5 unsuccessful drain attempts, the outbox parks an entry so
  // it stops retrying on its own. Until M4 these entries had no UI —
  // they sat in IDB forever and the user only knew about them via the
  // "Calendar sync — item parked" notification at the moment they
  // were parked. Surface them here with Retry / Discard so the user
  // can clear the queue without a devtools session.
  ensureParkedOutbox(ctx);
  if (Array.isArray(_parkedOutbox) && _parkedOutbox.length > 0) {
    rows.push(...renderParkedOutbox(ctx));
  }

  return rows;
}

// The Calendars heading's right-hand control: an "Add calendar" value button
// (no caret — a direct action, like Encryption's "Pair device"). Clicking
// expands the section and opens the provider picker.
function buildAddHeadingCta(ctx: SettingsSectionCtx, groupId: string): HTMLElement {
  return expandHeadingCta(ctx, groupId, 'Add calendar', {
    title: 'Add a calendar account',
    before: () => {
      if (!_newAccountState) _newAccountState = { kind: null, step: 'pick', error: '', fields: {} };
    },
  });
}

// Material "date_range" calendar glyph (fill:currentColor so it inherits the
// icon button's muted→text colour, incl. the .is-open accent when revealed).
const CAL_REVEAL_ICON =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-1.99.9-1.99 2L3 20c0 1.1.9 2 2 2h14c1.1 0 ' +
  '2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2zM7 12h5v5H7z"/></svg>';

// Reflect open/closed state on the reveal button — shared by the initial build
// and the row's updateControl patch (the retained control isn't rebuilt).
function syncRevealButton(btn: HTMLElement, open: boolean, name: string): void {
  syncRevealIcon(btn, open, `Hide calendars in ${name}`, `Calendars in ${name}`);
}

// An account row's right side: the muted provider label + a calendar-list reveal
// icon (the workspace members-icon pattern) + the "×" disconnect.
function buildAccountControl(ctx: SettingsSectionCtx, acct: any): HTMLElement {
  const subId = `cal-account:${acct.accountId}`;
  const wrap = document.createElement('span');
  wrap.className = 'settings-row-control';
  const kind = document.createElement('span');
  kind.className = 'settings-row-value';
  kind.textContent = providerKindLabel(acct.provider);

  // Reveal icon — toggles the account's calendar list (visibility checkboxes +
  // colours), exactly like the workspace members icon toggles the roster.
  const name = acct.label || acct.accountId;
  const reveal = buildRevealToggle(ctx, subId, {
    svg: CAL_REVEAL_ICON,
    cls: 'settings-cal-reveal',
    title: 'Calendars',
    openLabel: `Hide calendars in ${name}`,
    closedLabel: `Calendars in ${name}`,
  });

  const rm = removeButton({
    ariaLabel: `Disconnect ${acct.label || acct.accountId}`,
    title: 'Disconnect account',
  });
  rm.addEventListener('click', async () => {
    if (
      !(await confirmStatus(
        `Disconnect ${acct.label || acct.accountId}? Synced events stay in your daily notes.`,
        { confirmLabel: 'Disconnect', cancelLabel: 'Cancel' }
      ))
    )
      return;
    try {
      await ProviderRegistry?.remove?.(acct.accountId);
      // Clear the per-account calendar cache so the section collapses cleanly.
      _accountCalendarsCache.delete(acct.accountId);
      ctx.openExpansions.delete(subId);
    } catch (e: any) {
      console.warn('[settings] disconnect failed:', e);
    }
    ctx.invalidate();
  });
  wrap.append(kind, reveal, rm);
  return wrap;
}

/**
 * @param {any} kind
 * @returns {string} the human-readable provider label.
 */
function providerKindLabel(kind: any) {
  return (
    (
      {
        google: 'Google',
        microsoft: 'Microsoft',
        caldav: 'CalDAV',
        icloud: 'iCloud',
      } as any
    )[kind] ||
    kind ||
    'unknown'
  );
}

/**
 * @param {any} ctx
 * @param {any} acct - the connected account entry.
 * @returns {any[]} the per-account body rows.
 */
function renderAccountBody(ctx: SettingsSectionCtx, acct: any) {
  const rows = [];
  // The list of calendars for this account is lazy. We don't actively
  // poll providers from inside renderCalendars; the per-account cache lives
  // in a tiny module-scope map populated by ensureAccountCalendars().
  const cache = _accountCalendarsCache.get(acct.accountId);
  if (!cache) {
    ensureAccountCalendars(ctx, acct);
    rows.push(
      ctx.rawRow(() => makeP('Loading calendars…'), {
        key: `cal-loading:${acct.accountId}`,
        indent: true,
        searchText: 'loading calendars',
      })
    );
  } else if (cache.error) {
    rows.push(
      ctx.rawRow(() => makeP(`Couldn’t load calendars: ${cache.error}`), {
        key: `cal-error:${acct.accountId}`,
        indent: true,
        searchText: 'error',
      })
    );
  } else {
    for (const cal of cache.list) {
      // indent 1 (aligned with the account label) — the account row isn't a fold
      // group, so a deeper indent would paint an orphaned second accent-line slot.
      rows.push(
        ctx.rawRow(() => buildCalendarRow(ctx, acct, cal), {
          key: `cal:${acct.accountId}:${cal.id}`,
          indent: true,
          searchText: `cal ${cal.title}`,
        })
      );
    }
  }

  return rows;
}

// One calendar's visibility checkbox + colour picker. Built once + retained
// (the checkbox/picker are the source of truth for their own state).
function buildCalendarRow(ctx: SettingsSectionCtx, acct: any, cal: any): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'settings-inline-edit settings-calendar-row';
  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.className = 'app-switch-input'; // settings on/off boolean → switch (D3), not the task checkbox

  const selected = Settings?.get('calendars.selected', []) || [];
  cb.checked = selected.some((s: any) => s.accountId === acct.accountId && s.calendarId === cal.id);
  cb.addEventListener('change', async () => {
    const cur = (Settings?.get('calendars.selected', []) || []).slice();
    if (cb.checked) {
      if (!cur.some((s: any) => s.accountId === acct.accountId && s.calendarId === cal.id)) {
        cur.push({ accountId: acct.accountId, calendarId: cal.id, title: cal.title });
      }
    } else {
      const i = cur.findIndex(
        (s: any) => s.accountId === acct.accountId && s.calendarId === cal.id
      );
      if (i !== -1) cur.splice(i, 1);
    }
    try {
      await Settings?.set?.('calendars.selected', cur);
    } catch {
      // Settings.set rejects on a write failure; the store has already rolled
      // back + toasted. Swallow so this fire-and-forget change handler can't
      // leak an unhandled rejection. invalidate() below still re-renders from
      // the reverted persisted state.
    }
    ctx.invalidate();
  });
  const label = document.createElement('span');
  label.className = 'settings-row-text';
  label.textContent = cal.title;
  const colourKey = `${acct.accountId}:${cal.id}`;
  const colours = Settings?.get('calendars.colors', {}) || {};
  const picker = colorPicker(
    colours[colourKey] || cal.color || '#888888',
    async (v: any, phase: any) => {
      if (phase !== 'commit') return;
      const next = { ...(Settings?.get('calendars.colors', {}) || {}) };
      next[colourKey] = v;
      try {
        await Settings?.set?.('calendars.colors', next);
      } catch {
        // Settings.set rejects on a write failure; the store has already rolled
        // back + toasted. Swallow so this fire-and-forget colour-commit callback
        // can't leak an unhandled rejection.
      }
      if (typeof invalidateScheduleCache === 'function') invalidateScheduleCache();
    },
    `${cal.title} colour`
  );
  wrap.append(cb, label, picker);
  return wrap;
}

// Per-account calendar list cache. Keyed by accountId. Each entry is
// { list: [{id, title, color}], error?: string }.
const _accountCalendarsCache = new Map();

// Parked outbox cache. null while the first list() resolves; [] when
// empty; otherwise the entries themselves. ensureParkedOutbox kicks
// an async refresh; renderCalendars reads the cached value
// synchronously and re-renders once the refresh lands.
/** @type {any} */
let _parkedOutbox: any = null;
let _parkedOutboxLoading = false;
/**
 * @param {any} ctx
 * @returns {Promise<void>}
 */
async function ensureParkedOutbox(ctx: SettingsSectionCtx) {
  if (_parkedOutbox !== null || _parkedOutboxLoading) return;
  const Outbox = CalendarOutbox;
  if (!Outbox || typeof Outbox.list !== 'function') {
    _parkedOutbox = [];
    return;
  }
  _parkedOutboxLoading = true;
  try {
    _parkedOutbox = await Outbox.list({ parked: true });
  } catch (e: any) {
    console.warn('[settings] parked outbox list failed:', e?.message || e);
    _parkedOutbox = [];
  } finally {
    _parkedOutboxLoading = false;
    if (ctx.isOpened()) ctx.invalidate();
  }
}

/** @returns {void} */
function invalidateParkedOutboxCache() {
  _parkedOutbox = null;
  _parkedOutboxLoading = false;
}

// The Sync-window control: a <select> over rolling-lookback presets. Writing it
// invalidates the schedule cache so the next poll uses the new window. 0 = all
// history since first sync (no cap).
function buildSyncWindowSelect(): HTMLElement {
  const sel = document.createElement('select');
  sel.className = 'settings-mini-input';
  sel.setAttribute('aria-label', 'Calendar sync window — how far back to reconcile events');
  const presets: Array<[number, string]> = [
    [30, 'Last 30 days'],
    [90, 'Last 90 days'],
    [180, 'Last 180 days'],
    [365, 'Last year'],
    [0, 'All (since first sync)'],
  ];
  const current = Number(Settings?.get('calendars.syncLookbackDays', 90)) || 0;
  for (const [val, text] of presets) {
    const opt = document.createElement('option');
    opt.value = String(val);
    opt.textContent = text;
    if (val === current) opt.selected = true;
    sel.appendChild(opt);
  }
  sel.addEventListener('change', async () => {
    const v = Number(sel.value) || 0;
    try {
      await Settings?.set('calendars.syncLookbackDays', v);
    } catch {}
    if (typeof invalidateScheduleCache === 'function') invalidateScheduleCache();
  });
  return sel;
}

/**
 * @param {any} ctx
 * @returns {any[]} the parked-outbox rows.
 */
function renderParkedOutbox(ctx: SettingsSectionCtx) {
  const rows = [];
  const ID = 'group:calendars:parked';
  rows.push(
    ctx.row({
      id: ID,
      label: 'Parked sync items',
      value: `${_parkedOutbox.length}`,
      expandable: true,
      indent: true,
      cls: 'settings-row-warning',
      searchText: 'parked outbox calendar sync stuck failed retry discard',
    })
  );
  if (!ctx.openExpansions.has(ID)) return rows;

  rows.push(
    ctx.rawRow(
      () =>
        makeP(
          'These calendar edits failed five times in a row and have stopped retrying. ' +
            'Retry to try again now, or discard to drop them.'
        ),
      { key: 'cal-parked-intro', indent: 2 }
    )
  );

  for (const entry of _parkedOutbox) {
    rows.push(
      ctx.rawRow(() => buildParkedRow(ctx, entry), { key: `cal-parked:${entry.id}`, indent: 2 })
    );
  }
  return rows;
}

// One parked-outbox entry: summary + Retry/Discard (buttons self-disable).
function buildParkedRow(ctx: SettingsSectionCtx, entry: any): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'settings-parked-row';
  const label = document.createElement('div');
  label.className = 'settings-parked-label';
  label.textContent = summariseParkedEntry(entry);
  if (entry.lastError) {
    const err = document.createElement('div');
    err.className = 'settings-helptext settings-helptext-error';
    err.textContent = entry.lastError;
    label.appendChild(err);
  }
  const actions = document.createElement('div');
  actions.className = 'settings-parked-actions';

  const retry = document.createElement('button');
  retry.type = 'button';
  retry.className = 'btn';
  retry.textContent = 'Retry';
  retry.addEventListener('click', async () => {
    retry.disabled = true;
    discard.disabled = true;
    try {
      await CalendarOutbox.unpark(entry.id);
      // Kick a drain so the user sees the outcome immediately instead of waiting
      // for the 30-second timer.
      CalendarOutbox.drain?.(ProviderRegistry).catch((e: any) =>
        console.warn('[settings] parked drain failed:', e?.message || e)
      );
    } finally {
      invalidateParkedOutboxCache();
      ctx.invalidate();
    }
  });
  actions.appendChild(retry);

  const discard = document.createElement('button');
  discard.type = 'button';
  discard.className = 'btn btn--danger';
  discard.textContent = 'Discard';
  discard.addEventListener('click', async () => {
    retry.disabled = true;
    discard.disabled = true;
    try {
      await CalendarOutbox.remove(entry.id);
    } finally {
      invalidateParkedOutboxCache();
      ctx.invalidate();
    }
  });
  actions.appendChild(discard);

  wrap.append(label, actions);
  return wrap;
}

/**
 * @param {any} entry - a parked outbox entry.
 * @returns {string} a one-line summary.
 */
function summariseParkedEntry(entry: any) {
  const acct = entry.accountId || 'unknown account';
  const cal = entry.calendarId || '';
  const op = entry.op || 'op';
  const title = entry.payload?.event?.title;
  const subject = title
    ? `“${title}”`
    : entry.payload?.providerId
      ? `event ${entry.payload.providerId}`
      : 'an event';
  return `${op} ${subject} on ${acct}${cal ? ` / ${cal}` : ''}`;
}

/**
 * @param {any} ctx
 * @param {any} acct
 * @returns {Promise<void>}
 */
async function ensureAccountCalendars(ctx: SettingsSectionCtx, acct: any) {
  if (_accountCalendarsCache.has(acct.accountId)) return;
  _accountCalendarsCache.set(acct.accountId, { list: [] }); // placeholder
  let inst = ProviderRegistry?.get?.(acct.accountId);
  if (!inst) {
    // Providers hydrate asynchronously after boot; if this render beat the
    // hydration, force a reconcile and re-check before giving up — otherwise a
    // race strands the account on a stale "provider not loaded".
    try {
      await ProviderRegistry?.reload?.();
    } catch {}
    inst = ProviderRegistry?.get?.(acct.accountId);
  }
  if (!inst) {
    _accountCalendarsCache.set(acct.accountId, { list: [], error: 'provider not loaded' });
    if (ctx.isOpened()) ctx.invalidate();
    return;
  }
  try {
    const cals = await inst.listCalendars();
    _accountCalendarsCache.set(acct.accountId, { list: cals || [] });
  } catch (e: any) {
    _accountCalendarsCache.set(acct.accountId, { list: [], error: e?.message || String(e) });
  }
  if (ctx.isOpened()) ctx.invalidate();
}

// ── Add-account inline form ─────────────────────────────────────────────
/**
 * @param {any} ctx
 * @returns {any[]} the add-account inline-form rows.
 */
// Add-account form. STRUCTURAL state (the step) is in the key, so a step change
// cleanly remounts; ATTRIBUTE state (busy → Connect disabled, error text) is
// patched by the step's `update` on the retained node, so the CalDAV inputs keep
// focus + value across the busy/error re-renders.
function renderAddAccountForm(ctx: SettingsSectionCtx) {
  const state = _newAccountState;
  const rows: any[] = [];

  if (state.step === 'pick') {
    rows.push(
      ctx.rawRow(() => buildPickForm(ctx, state), {
        key: 'cal-add:pick',
        indent: true,
        searchText: 'add account picker',
      })
    );
  } else if (state.step === 'caldav-form') {
    rows.push(
      ctx.rawRow(() => buildCaldavForm(ctx, state), {
        key: 'cal-add:caldav-form',
        update: updateCaldavForm,
        indent: true,
        searchText: 'caldav icloud form',
      })
    );
    if (state.kind === 'icloud') {
      rows.push(
        ctx.rawRow(buildIcloudHelp, {
          key: 'cal-add:caldav-help',
          indent: true,
          searchText: 'help icloud password',
        })
      );
    }
  } else if (state.step === 'oauth') {
    rows.push(
      ctx.rawRow(() => buildOauthForm(ctx, state), {
        key: 'cal-add:oauth',
        update: updateOauthForm,
        indent: true,
        searchText: 'oauth in progress',
      })
    );
  }
  return rows;
}

// The add-account flow's "Cancel" link — identical across the picker, the CalDAV
// form, and the OAuth step: clear the in-flight state and re-render.
function buildCancelLink(ctx: SettingsSectionCtx): HTMLButtonElement {
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'settings-btn-link';
  cancel.textContent = 'Cancel';
  cancel.addEventListener('click', () => {
    _newAccountState = null;
    ctx.invalidate();
  });
  return cancel;
}

function buildPickForm(ctx: SettingsSectionCtx, state: any): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'settings-inline-edit settings-account-picker';
  const heading = document.createElement('div');
  heading.className = 'settings-row-text';
  heading.textContent = 'Connect a calendar from:';
  wrap.appendChild(heading);
  // Per-vendor presets — pre-populate the server URL so the user only has to
  // supply credentials. The host strings here must match the relay allow-list in
  // transport.js / caldav-relay/index.ts.
  const CALDAV_PRESETS = [
    {
      kind: 'icloud',
      label: 'iCloud (CalDAV)',
      server: window.ICLOUD_CALDAV_BASE || 'https://caldav.icloud.com/',
    }, // runtime — set by icloud-provider.js, no ES export
    { kind: 'caldav', label: 'Fastmail', server: 'https://caldav.fastmail.com/' },
    { kind: 'caldav', label: 'Other CalDAV (Nextcloud, self-hosted)', server: '' },
    { kind: 'google', label: 'Google Calendar', server: null },
    { kind: 'microsoft', label: 'Microsoft 365 / Outlook', server: null },
  ];
  for (const { kind, label, server } of CALDAV_PRESETS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn';
    btn.textContent = label;
    btn.addEventListener('click', () => {
      state.kind = kind;
      if (kind === 'icloud' || kind === 'caldav') {
        state.step = 'caldav-form';
        state.fields = { serverUrl: server || '', user: '', pass: '' };
        state.preset = label;
      } else {
        state.step = 'oauth';
        state._started = false;
      }
      ctx.invalidate();
    });
    wrap.appendChild(btn);
  }
  wrap.appendChild(buildCancelLink(ctx));
  return wrap;
}

function buildCaldavForm(ctx: SettingsSectionCtx, state: any): HTMLElement {
  const form = document.createElement('div');
  form.className = 'settings-inline-edit settings-caldav-form';

  const url = document.createElement('input');
  url.type = 'url';
  url.placeholder = 'https://caldav.example.com/';
  url.value = state.fields.serverUrl || '';
  // Lock the server URL for the iCloud and Fastmail presets so users can't typo a
  // host the relay won't allow; "Other CalDAV" leaves it editable.
  url.disabled = state.kind === 'icloud' || state.preset === 'Fastmail';
  url.addEventListener('input', () => {
    state.fields.serverUrl = url.value;
  });

  const user = document.createElement('input');
  user.type = 'text';
  user.placeholder = state.kind === 'icloud' ? 'Apple ID' : 'username';
  user.value = state.fields.user || '';
  user.addEventListener('input', () => {
    state.fields.user = user.value;
  });

  const pass = document.createElement('input');
  pass.type = 'password';
  pass.placeholder = state.kind === 'icloud' ? 'app-specific password' : 'password';
  pass.value = state.fields.pass || '';
  pass.addEventListener('input', () => {
    state.fields.pass = pass.value;
  });

  const connect = document.createElement('button');
  connect.type = 'button';
  connect.textContent = 'Connect';
  connect.className = 'btn btn--primary';
  connect.disabled = _accountsBusy;
  connect.addEventListener('click', () => connectCaldav(ctx, state));

  // Always-present error (hidden when empty) so updateCaldavForm can toggle it
  // without remounting the focused inputs.
  const err = errorDiv(state.error || '');

  form.append(url, user, pass, connect, buildCancelLink(ctx), err);
  return form;
}

function updateCaldavForm(el: HTMLElement): void {
  const connect = el.querySelector('.btn.btn--primary') as HTMLButtonElement | null;
  if (connect) connect.disabled = _accountsBusy;
  setErrorDiv(el.querySelector('.settings-error'), _newAccountState?.error || '');
}

function buildIcloudHelp(): HTMLElement {
  const help = document.createElement('div');
  help.className = 'settings-help';
  help.innerHTML =
    'iCloud requires an app-specific password. Generate one at <a href="https://appleid.apple.com/account/manage" target="_blank" rel="noopener">appleid.apple.com</a> → Sign-in and Security → App-Specific Passwords.';
  return help;
}

function buildOauthForm(ctx: SettingsSectionCtx, state: any): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'settings-inline-edit';
  const msg = document.createElement('div');
  msg.className = 'settings-row-text';
  msg.textContent = `Connecting via ${providerKindLabel(state.kind)}…`;
  const err = errorDiv(state.error || '');
  wrap.append(msg, buildCancelLink(ctx), err);

  // Kick the flow once, on mount (entering the oauth step).
  if (!state._started) {
    state._started = true;
    startOauth(ctx, state).catch((e: any) => {
      state.step = 'pick';
      state.error = e?.message || 'OAuth failed.';
      ctx.invalidate();
    });
  }
  return wrap;
}

function updateOauthForm(el: HTMLElement): void {
  setErrorDiv(el.querySelector('.settings-error'), _newAccountState?.error || '');
}

/**
 * @param {any} ctx
 * @param {any} state - the add-account form state.
 * @returns {Promise<void>}
 */
async function connectCaldav(ctx: SettingsSectionCtx, state: any) {
  if (_accountsBusy) return;
  if (!state.fields.user || !state.fields.pass || !state.fields.serverUrl) {
    state.error = 'All three fields are required.';
    ctx.invalidate();
    return;
  }
  _accountsBusy = true;
  state.error = '';
  ctx.invalidate();
  try {
    const host = (() => {
      try {
        return new URL(state.fields.serverUrl).host;
      } catch {
        return state.fields.serverUrl;
      }
    })();
    const accountId = `${state.kind}:${host}:${state.fields.user}`;
    const credentialKey = accountId;
    // Stash credentials in the workspace vault. Format mirrors the
    // structure CalDavProvider._loadCreds expects.
    const vault = WorkspaceSessionVault;
    if (!vault?.save) throw new Error('credential vault unavailable');
    await vault.save(
      credentialKey,
      JSON.stringify({
        user: state.fields.user,
        pass: state.fields.pass,
      })
    );
    await ProviderRegistry?.add?.({
      accountId,
      provider: state.kind,
      label: `${state.fields.user}${state.kind === 'icloud' ? ' (iCloud)' : ''}`,
      credentialKey,
      serverUrl: state.fields.serverUrl,
    });
    await ProviderRegistry?.reload?.();
    _newAccountState = null;
  } catch (e: any) {
    state.error = e?.message || String(e);
  } finally {
    _accountsBusy = false;
    ctx.invalidate();
  }
}

/**
 * @param {any} ctx
 * @param {any} state - the add-account form state.
 * @returns {Promise<void>}
 */
async function startOauth(ctx: SettingsSectionCtx, state: any) {
  if (!CalendarOAuth?.connect) {
    state.error = 'OAuth not configured for this build.';
    state.step = 'pick';
    ctx.invalidate();
    return;
  }
  const result = await CalendarOAuth.connect(state.kind);
  if (!result?.accountId) {
    state.error = 'OAuth cancelled.';
    state.step = 'pick';
    ctx.invalidate();
    return;
  }
  await ProviderRegistry?.reload?.();
  _newAccountState = null;
  ctx.invalidate();
}
